import type { Substance } from '../pharmacology/types';
import { requireElement } from './domHelpers';

/**
 * Manages the dose and time sliders. The dose slider's min/max/step/default
 * are reconfigured per-substance from `substance.doseRange` — real doses
 * span micrograms (LSD) to tens of grams (ethanol), so a single fixed-unit
 * range shared across all substances would be unusable for most of them.
 * The time slider (0-24h) is generic and does not change per substance.
 */
export class DoseTimeControls {
  private readonly doseSlider: HTMLInputElement;
  private readonly doseValueEl: HTMLElement;
  private readonly timeSlider: HTMLInputElement;
  private readonly timeValueEl: HTMLElement;

  private currentSubstance: Substance | null = null;

  constructor(private readonly onChange: (doseOrReleaseLevel: number, tHours: number) => void) {
    this.doseSlider = requireElement<HTMLInputElement>('dose-slider');
    this.doseValueEl = requireElement('dose-value');
    this.timeSlider = requireElement<HTMLInputElement>('time-slider');
    this.timeValueEl = requireElement('time-value');

    this.doseSlider.addEventListener('input', () => this.emitChange());
    this.timeSlider.addEventListener('input', () => this.emitChange());
  }

  setSubstance(substance: Substance | null): void {
    this.currentSubstance = substance;

    if (!substance) {
      this.doseSlider.disabled = true;
      this.doseValueEl.textContent = '—';
      this.onChange(0, this.currentTimeHours());
      return;
    }

    const { min, max, default: def, step } = substance.doseRange;
    this.doseSlider.disabled = false;
    this.doseSlider.min = String(min);
    this.doseSlider.max = String(max);
    this.doseSlider.step = String(step);
    this.doseSlider.value = String(def);

    this.emitChange();
  }

  private currentTimeHours(): number {
    return Number(this.timeSlider.value);
  }

  private emitChange(): void {
    const tHours = this.currentTimeHours();
    this.timeValueEl.textContent = `${tHours.toFixed(1)}h`;

    if (!this.currentSubstance) {
      this.doseValueEl.textContent = '—';
      this.onChange(0, tHours);
      return;
    }

    const rawDose = Number(this.doseSlider.value);
    const { unit, displayScale } = this.currentSubstance.doseRange;
    const displayValue = rawDose * displayScale;
    this.doseValueEl.textContent = `${formatNumber(displayValue)} ${unit}`;
    this.onChange(rawDose, tHours);
  }
}

function formatNumber(value: number): string {
  if (value >= 100) return value.toFixed(0);
  if (value >= 1) return value.toFixed(1);
  return value.toFixed(3);
}
